import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { FaCheckCircle, FaArrowRight, FaGlobe, FaLightbulb, FaHandshake, FaTrophy } from "react-icons/fa";

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, delay: i * 0.1 },
  }),
};

export default function About() {
  return (
    <main className="bg-white text-gray-800 font-sans">

      {/* ── Hero ── */}
      <section className="bg-gradient-to-br from-blue-900 to-blue-800 text-white py-24 px-6 xl:px-24">
        <div className="max-w-4xl mx-auto text-center">
          <motion.p
            className="text-sky-300 text-sm font-semibold uppercase tracking-widest mb-4"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }}
          >
            About Us
          </motion.p>
          <motion.h1
            className="text-4xl md:text-5xl font-extrabold mb-6"
            initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}
          >
            Engineering the Future of Regulated Manufacturing
          </motion.h1>
          <motion.p
            className="text-blue-200 text-lg max-w-2xl mx-auto"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3 }}
          >
            XKMINNE Technologies was built by practitioners — people who have operated inside the systems we now transform. We combine deep domain expertise with AI-first thinking to help life sciences and industrial companies compete in a digital world.
          </motion.p>
        </div>
      </section>

      {/* ── Mission & Story ── */}
      <section className="py-24 px-6 xl:px-24 bg-white">
        <div className="max-w-5xl mx-auto grid md:grid-cols-2 gap-14 items-center">
          <motion.div
            variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }}
          >
            <p className="text-sky-600 text-sm font-semibold uppercase tracking-widest mb-3">Our Mission</p>
            <h2 className="text-3xl font-bold text-blue-900 mb-5">
              Making Intelligent Operations Accessible to Every Regulated Enterprise
            </h2>
            <p className="text-gray-600 leading-relaxed mb-4">
              Too many life sciences and manufacturing companies are held back by legacy processes, siloed data, and compliance risk. We founded XKMINNE to change that — by making the same AI-powered operational intelligence that the world's largest enterprises use available to organizations of all sizes.
            </p>
            <p className="text-gray-600 leading-relaxed">
              Every engagement starts with a single question: what measurable outcome does your business need? We then design and deploy the technology to get you there — on time, on budget, and audit-ready.
            </p>
          </motion.div>
          <motion.div
            variants={fadeUp} custom={1} initial="hidden" whileInView="visible" viewport={{ once: true }}
            className="bg-gradient-to-br from-sky-50 to-blue-50 rounded-2xl p-8 border border-blue-100"
          >
            <h3 className="font-bold text-blue-900 mb-5 text-lg">What Sets Us Apart</h3>
            <ul className="space-y-4">
              {[
                "Founded by practitioners with 10+ years inside regulated operations",
                "Compliance-first architecture — FDA, ISO, GxP built in from day one",
                "Proprietary rapid deployment methodology — 6-week average go-live",
                "Deep SAP expertise combined with modern AI and LLM capabilities",
                "Global delivery experience across US, EU, and APAC markets",
              ].map((item, i) => (
                <li key={i} className="flex items-start gap-3 text-sm text-gray-700">
                  <FaCheckCircle className="text-emerald-500 mt-0.5 flex-shrink-0" />
                  {item}
                </li>
              ))}
            </ul>
          </motion.div>
        </div>
      </section>

      {/* ── Values ── */}
      <section className="py-24 px-6 xl:px-24 bg-gray-50">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-14">
            <p className="text-sky-600 text-sm font-semibold uppercase tracking-widest mb-2">Our Values</p>
            <h2 className="text-3xl font-bold text-blue-900">The Principles That Guide Every Engagement</h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                icon: <FaHandshake className="text-2xl text-sky-600" />,
                title: "Client Outcomes First",
                desc: "We measure success by your results — not hours billed or features shipped. Every decision is made with your ROI in mind.",
              },
              {
                icon: <FaLightbulb className="text-2xl text-amber-500" />,
                title: "Practical Innovation",
                desc: "We adopt new technology when it solves real problems, not to follow trends. AI is a tool to create value, not a buzzword.",
              },
              {
                icon: <FaTrophy className="text-2xl text-emerald-500" />,
                title: "Excellence in Execution",
                desc: "We bring the same rigor to our delivery that our clients bring to their manufacturing operations — precision, documentation, and accountability.",
              },
              {
                icon: <FaGlobe className="text-2xl text-violet-500" />,
                title: "Global Perspective",
                desc: "With experience across US FDA, EU MDR, and APAC regulatory environments, we understand the complexity of global compliance.",
              },
            ].map((v, i) => (
              <motion.div
                key={i}
                custom={i}
                variants={fadeUp}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                className="bg-white p-6 rounded-xl border border-gray-100 shadow-sm"
              >
                <div className="mb-4 p-2.5 inline-flex rounded-lg bg-gray-50">{v.icon}</div>
                <h3 className="font-bold text-gray-900 mb-2">{v.title}</h3>
                <p className="text-sm text-gray-500 leading-relaxed">{v.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Milestones ── */}
      <section className="py-24 px-6 xl:px-24 bg-white">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-14">
            <p className="text-sky-600 text-sm font-semibold uppercase tracking-widest mb-2">Our Track Record</p>
            <h2 className="text-3xl font-bold text-blue-900">Built on Proven Delivery</h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            {[
              { value: "10+", label: "Years of regulated operations expertise" },
              { value: "3", label: "Global life sciences clients served" },
              { value: "100%", label: "FDA audit pass rate across client deployments" },
              { value: "USA", label: "Incorporated and operating in the United States" },
            ].map((m, i) => (
              <motion.div
                key={i}
                custom={i}
                variants={fadeUp}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                className="p-6 rounded-2xl bg-gradient-to-br from-sky-50 to-blue-50 border border-blue-100"
              >
                <div className="text-3xl font-extrabold text-blue-800 mb-2">{m.value}</div>
                <p className="text-xs text-gray-600 leading-snug">{m.label}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="py-24 bg-blue-900 text-white text-center px-6">
        <div className="max-w-2xl mx-auto">
          <h2 className="text-3xl font-extrabold mb-4">Ready to Work With Us?</h2>
          <p className="text-blue-200 mb-8">
            Let's explore how XKMINNE can help your organization achieve its digital transformation goals. Schedule a free 30-minute consultation with our team.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact"
              className="inline-flex items-center justify-center gap-2 bg-white text-blue-800 font-bold px-8 py-4 rounded-lg shadow-lg hover:bg-sky-50 transition-colors"
            >
              Schedule a Consultation <FaArrowRight />
            </Link>
            <Link
              to="/founder"
              className="inline-flex items-center justify-center border border-white/40 text-white font-semibold px-8 py-4 rounded-lg hover:bg-white/10 transition-colors"
            >
              Meet the Founder
            </Link>
          </div>
        </div>
      </section>

      {/* ── Footer ── */}
      <footer className="bg-gray-900 text-gray-400 py-12 px-6 text-center">
        <span className="text-xl font-extrabold text-white">
          XK<span className="text-sky-400">MINNE</span>
        </span>
        <p className="text-sm mt-3 text-gray-500">
          &copy; {new Date().getFullYear()} XKMINNE Technologies. All rights reserved.
        </p>
      </footer>
    </main>
  );
}
